/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Ladda from 'ladda';
import { View } from 'backbone.marionette';
import _ from 'lodash';
import Radio from 'backbone.radio';

import { formatDisplayDate } from '@/util/MomentUtils';
import lazyLoadView from '@/util/lazyLoadView';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import FileUploadModel from '@/fileupload/FileUploadModel';
import DownloadDocumentInternal from '@/util/DownloadDocumentInternal';

import GetExternalUserCommands from '../../../external-dashboard/services/GetExternalUserCommands';
import IntDashboardSidebarNavView from '../../../internal-dashboard/views/IntDashboardSidebarNavView';
import SearchAssets from '../../services/SearchAssets';
import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import InventoryDashboardGridView from '../../views/InventoryDashboardGridView';
import AMRFileUploadTmpl from '../../../common/fileupload/AMRFileUploadTmpl.hbs';
import GetLatestReconciledAMRRecord from '../../services/GetLatestReconciledAMRRecord';

import tmplInventoryDashboardPage from './InventoryDashboardPage.hbs';

import './InventoryDashboardPage.css';

const InventoryDashboardPage = View.extend({
  template: tmplInventoryDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    searchFieldsView: '.search-fields-view',
    inventoryGrid: '#inventoryGrid',
    btnDownloadCSV: '.btn-download-csv',
    btnUploadAMR: '.btn-upload-amr',
    btnDownloadResults: '.btn-download-results',
    btnAssetChangeRequest: '.btn-asset-change-request',
    btnCreateChangeRequest: '.btn-create-change-request',
    instructionCardContainer: '.instruction-card-container'
  },

  regions: {
    sideBar: '@ui.sideBar',
    searchFieldsView: '@ui.searchFieldsView',
    inventoryGrid: '@ui.inventoryGrid',
  },

  events: {
    'click @ui.btnDownloadCSV': 'onClickDownloadCSV',
    'click @ui.btnUploadAMR': 'onClickUploadAMR',
    'click @ui.btnDownloadResults': 'onClickDownloadResults',
    'click @ui.btnAssetChangeRequest': 'onClickStartAssetChangeRequest',
    'click @ui.btnCreateChangeRequest': 'onOpenCreateAssetChangeRequest',
    'change #file-upload-input': 'onFileSelect'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  onSearchResponse(response) {
    this.$('.result-count').empty().append(`<span><b>Total Result Count: ${response.totalCount}</b></span>`);
    this.inventoryGrid.collection.fullCollection.reset(response.assessments);
    this.inventoryGrid.getViewGrid().sort('submittedOn', 'descending');
  },

  templateContext() {
    const isInternalUser = !EXTERNAL;
    const lastAmrDate = formatDisplayDate(this.model.get('amr')?.processedOn);
    return {
      isInternalUser,
      lastAmrDate
    };
  },

  onRender() {
    if (!EXTERNAL) {
      this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'inventory' }));
    }

    let searchFields = [
      {
        id: 'command-name-search-field',
        name: 'facilityText',
        class: 'InputSearchField',
        placeholder: 'Command',
      },
      {
        id: 'command-search-field',
        name: 'command',
        class: 'SelectSearchField',
        multiple: true,
        'data-parsley-required': true,
        lookupService: GetExternalUserCommands,
        lookupMapping: (command) => {
          return { name: command.name, id: command.name };
        },
        width: 2
      },
      {
        id: 'serial-number-search-field',
        name: 'serialNumber',
        class: 'InputSearchField',
        placeholder: 'Serial Number',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'inventory-number-search-field',
        name: 'inventoryNumber',
        class: 'InputSearchField',
        placeholder: 'Inventory Number',
        width: 3
      },
      {
        id: 'location-search-field',
        name: 'location',
        class: 'InputSearchField',
        placeholder: 'Location',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'unique-id-search-field',
        name: 'uniqueId',
        class: 'InputSearchField',
        placeholder: 'G-Invoicing Unique Order ID',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'g-invoicing-search-field',
        name: 'gInvoicing',
        class: 'InputSearchField',
        placeholder: 'G-Invoicing Order Number',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'g-invoicing-line-item-number',
        name: 'lineItemNumber',
        class: 'InputSearchField',
        placeholder: 'G-Invoicing Line Item Number',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'piin-search-field',
        name: 'piin',
        class: 'InputSearchField',
        placeholder: 'PIIN/SPIIN',
        width: 3
      },
      {
        id: 'mipr-number-search-field',
        name: 'miprNumber',
        class: 'InputSearchField',
        placeholder: 'MIPR Number',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'mipr-search-field',
        name: 'miprCode',
        class: 'InputSearchField',
        placeholder: 'MIPR Code',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'make-search-field',
        name: 'make',
        class: 'InputSearchField',
        placeholder: 'Make',
        width: EXTERNAL ? 2 : 3
      },
      {
        id: 'model-search-field',
        name: 'model',
        class: 'InputSearchField',
        placeholder: 'Model',
        width: EXTERNAL ? 2: 3
      },
      {
        id: 'original-value-min-search-field',
        name: 'originalValueMin',
        class: 'InputSearchField',
        placeholder: 'Original Value Min',
        width: EXTERNAL ? 2 : 6
      },
      {
        id: 'original-value-max-search-field',
        name: 'originalValueMax',
        class: 'InputSearchField',
        placeholder: 'Original Value Max',
        width: EXTERNAL ? 2 : 6
      }
    ];

    if (EXTERNAL) {
      const internalOnlySearchFields = [
        'command-name-search-field',
        'piin-search-field',
        'inventory-number-search-field'
      ];

      searchFields = _.filter(searchFields, (col) => {
        return !_.includes(internalOnlySearchFields, col.id);
      });
    } else {
      searchFields = _.filter(searchFields, (col) => {
        return !_.includes('command-search-field', col.id);
      });
    }

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchAssets))
    );

    this.inventoryGrid = new InventoryDashboardGridView({
      pageSize: 50
    });

    this.showChildView('inventoryGrid', this.inventoryGrid);

    this.ui.instructionCardContainer.hide();
    this.ui.btnCreateChangeRequest.hide();
  },

  async onClickDownloadCSV(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnDownloadCSV[0]);
    spinner.start();
    await this.inventoryGrid.generateAndDownloadCSV();
    spinner.stop();
  },

  async onClickUploadAMR(e) {
    e.preventDefault();
    this.$('#file-upload-input').trigger('click');
  },

  async onClickDownloadResults(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnDownloadResults[0]);
    spinner.start();
    const amrRecord = await GetLatestReconciledAMRRecord();
    DownloadDocumentInternal({
      fileId: amrRecord.reconciledCSFileId,
      fileName: 'orders_vs_assets_results.xlsx'
    });
    spinner.stop();
  },

  async onFileSelect(e) {
    e.preventDefault();
    const files = e.target.files || e.originalEvent.dataTransfer.files;
    const file = files[0];
    const spinner = Ladda.create(this.ui.btnUploadAMR[0]);
    spinner.start();
    await this.uploadFile(file);
    spinner.stop();
    this.showUploadSuccess();
    this.trigger('file:uploaded');
  },

  async uploadFile(file) {
    const fileUploadModel = new FileUploadModel({
      file1: file,
      fileName: file.name,
      showError: this.showUploadError,
      pathToFileId: 'html.head.script.Envelope.Body.UploadAMRResponse.FileId.__text',
      tmplMethodXML: AMRFileUploadTmpl
    });

    await fileUploadModel.upload();
  },

  async onClickStartAssetChangeRequest(e) {
    e.preventDefault();

    await this.inventoryGrid.showSelectColumn();
    this.ui.instructionCardContainer.show();
    this.ui.btnCreateChangeRequest.show();
    this.ui.btnAssetChangeRequest.hide();
  },

  async onOpenCreateAssetChangeRequest(e) {
    const selectedModels = this.inventoryGrid.viewGrid.getSelectedModels();

    if (selectedModels.length < 1 ) {
      this.showAssetChangeError();
    } else {
      const assets = _.reduce(
        selectedModels,
        (arr, asset) => {
          if(asset) {
            arr.push(asset.toJSON());
          }
          return arr;
        },
        []
      );
  
      Radio.channel('navigate').request('show:asset:change:request', assets);
    }
  },

  showUploadError() {
    showErrorMessage({
      errorCode: 'fileUploadError',
      errorTitle: 'File Upload Error',
      errorMessage: 'An error occurred while uploading the AMR. Please contact an administrator.'
    });
  },

  showUploadSuccess() {
    showSuccessMessage({
      code: 'fileUploadSuccess',
      title: 'File Upload Successfully',
      message: 'The AMR has been successfully uploaded. It will be processed and the inventory will be updated at approximately 0700 GMT.',
      hideAfter: 5
    });
  },

  showAssetChangeError() {
    showErrorMessage({
      errorCode: 'assetChangeError',
      errorTitle: 'Asset Change Request Error',
      errorMessage: 'You must select one or more assets in order to submit a change request.'
    });
  }
});

export default InventoryDashboardPage;
